/**
 * Round 6 (WP3): one place decides how a run reads — its chip, its trigger tag, its meter, who
 * started it — so the distributions list, the runs list and the run page say the same thing
 * (prototype `runState`). Pure; RunSummaryApi in, generic component props out.
 */
import {
  DISTRIBUTION_SHARE_NOTE_COPY as SHARE_NOTE,
  DISTRIBUTION_LIST_RUNS_COPY as LIST_RUNS,
  DISTRIBUTION_LIVE_COPY as LIVE,
  DISTRIBUTION_RUN_LIST_COPY as RUNS,
  DISTRIBUTION_RUN_STATE_COPY as STATE,
  DISTRIBUTION_RUN_TRIGGER_LABEL as TRIGGER,
} from "../../constants/distributionRunsCopy";
import type { ProgressPanelProps } from "../../components/molecules/ProgressPanel/ProgressPanel.types";
import type { ProgressStep } from "../../components/molecules/ProgressSteps/ProgressSteps.types";
import type { StackedMeterSegment } from "../../components/atoms/StackedMeter/StackedMeter.types";
import type { RunHistoryRow } from "../../components/molecules/RunHistoryTable/RunHistoryTable.types";
import type { StatusChipProps } from "../../components/molecules/StatusChip/StatusChip.types";
import { formatMonthDay, formatSendMoment, formatShortDate, formatTimeOfDay, initialsOf } from "./distributionFormat";
import { runProgressShare } from "./distributionNotifications";
import { runProgressOf } from "./distributionRunProgress";

import type { DistributionSummaryApi, RunDetailApi, RunStatusApi, RunSummaryApi, RunTriggerApi } from "./distributions.types";

/** live: going · failed: nothing sent · preview · attn: someone missed out · ok: delivered (or a test sent). */
export type RunStateKey = "live" | "failed" | "preview" | "attn" | "ok";
/** The runs list's segments. */
export type RunCategory = "all" | "attn" | "ok" | "test";

export interface RunStateView {
  key: RunStateKey;
  chip: Omit<StatusChipProps, "className">;
  /** "Nothing sent", "2 didn’t arrive · 1 held", "6 of 8 sent · 1 skipped, no data". */
  detail: string | null;
}

export const isLiveStatus = (status: RunStatusApi): boolean => status === "queued" || status === "running";

/** Every delivery of the run, whatever became of it. */
export const runTotal = ({ counts }: Pick<RunSummaryApi, "counts">): number => counts.sent + counts.failed + counts.skipped + counts.held;

export const runProblemDetail = ({ counts }: Pick<RunSummaryApi, "counts">): string[] =>
  [counts.failed ? STATE.didntArrive(counts.failed) : null, counts.held ? STATE.held(counts.held) : null].filter((bit): bit is string => Boolean(bit));

export const runStateOf = (run: Pick<RunSummaryApi, "status" | "trigger" | "counts" | "requested_by">): RunStateView => {
  if (isLiveStatus(run.status)) {
    return { key: "live", chip: { tone: "info", label: run.status === "queued" ? STATE.queued : STATE.running, live: true }, detail: null };
  }
  if (run.status === "failed" && run.counts.sent === 0) {
    return { key: "failed", chip: { tone: "error", label: STATE.failed, icon: "alert-circle" }, detail: STATE.nothingSent };
  }
  const problems = runProblemDetail(run);
  if (run.trigger === "preview") {
    return { key: "preview", chip: { tone: "neutral", label: STATE.preview, icon: "eye" }, detail: [STATE.nothingSent, ...problems].join(" · ") };
  }
  if (problems.length) {
    return { key: "attn", chip: { tone: "error", label: STATE.attention, icon: "alert-circle" }, detail: problems.join(" · ") };
  }
  if (run.trigger === "test") {
    const who = run.requested_by?.name;
    return { key: "ok", chip: { tone: "success", label: STATE.testSent, icon: "check-circle" }, detail: who ? STATE.sentTo(who) : null };
  }
  const total = runTotal(run);
  const noData = run.counts.skipped ? ` · ${STATE.skippedNoData(run.counts.skipped)}` : "";
  return { key: "ok", chip: { tone: "success", label: STATE.delivered, icon: "check-circle" }, detail: `${STATE.sentOf(run.counts.sent, total)}${noData}` };
};

/** A finished test or preview run (given its trigger) is a "test", whatever its outcome, so previews never count as delivered; a live run is "other". */
export const runCategoryOf = (state: RunStateKey, trigger?: RunTriggerApi): Exclude<RunCategory, "all"> | "other" =>
  state !== "live" && (trigger === "test" || trigger === "preview") ? "test" : state === "attn" || state === "failed" ? "attn" : state === "ok" ? "ok" : "other";

export const triggerTagOf = (trigger: RunTriggerApi): RunHistoryRow["trigger"] => {
  switch (trigger) {
    case "now":
      return { label: TRIGGER.now, icon: "send" };
    case "test":
      return { label: TRIGGER.test, icon: "mail", dashed: true };
    case "preview":
      return { label: TRIGGER.preview, icon: "eye", dashed: true };
    case "schedule":
    default:
      return { label: TRIGGER.schedule, icon: "clock" };
  }
};

/** How the run's deliveries split, for the thin bar beside its numbers. Nothing for a run that failed outright. */
export const runMeterOf = (run: Pick<RunSummaryApi, "status" | "counts">): StackedMeterSegment[] => {
  if (run.status === "failed" && run.counts.sent === 0) return [];
  return [
    { value: run.counts.sent, tone: "success" },
    { value: run.counts.failed, tone: "error" },
    { value: run.counts.held, tone: "warning" },
    { value: run.counts.skipped, tone: "muted" },
  ];
};

/** When it ran, else when it was due, else when it was queued. */
export const runMomentIso = (run: Pick<RunSummaryApi, "started_at" | "scheduled_for" | "finished_at"> & { queued_at?: string | null }): string | null =>
  run.started_at ?? run.scheduled_for ?? run.queued_at ?? run.finished_at;

/** "Tue, Oct 13 · 8:00 AM PT" */
export const runLabelOf = (run: Pick<RunSummaryApi, "started_at" | "scheduled_for" | "finished_at"> & { queued_at?: string | null }, timeZone: string): string => {
  const at = runMomentIso(run);
  return at ? formatSendMoment(at, timeZone) : STATE.queued;
};

export const startedByOf = (run: Pick<RunSummaryApi, "trigger" | "requested_by">): RunHistoryRow["startedBy"] => {
  if (run.trigger === "schedule" || !run.requested_by) {
    return run.trigger === "schedule"
      ? { name: STATE.schedule, system: true }
      : { name: STATE.someone, initials: initialsOf(STATE.someone) };
  }
  const name = run.requested_by.name || run.requested_by.email || STATE.someone;
  return { name, initials: initialsOf(name) };
};

const count = (value: number, tone: "error" | "warning" = "error") =>
  value ? { value: value.toLocaleString(), tone } : { value: "0", tone: "muted" as const };

const DASH = { value: "—", tone: "muted" as const };

/** One row of the runs list. */
export const toRunHistoryRow = (run: RunSummaryApi, timeZone: string): RunHistoryRow => {
  const state = runStateOf(run);
  const label = runLabelOf(run, timeZone);
  const failedOutright = state.key === "failed";
  const preview = run.trigger === "preview";
  return {
    id: run.id,
    label,
    current: state.key === "live",
    trigger: triggerTagOf(run.trigger),
    status: { ...state.chip, detail: state.detail },
    meter: runMeterOf(run),
    counts: {
      made: state.key === "live" ? { value: `${run.counts.copies}` } : { value: run.counts.copies.toLocaleString() },
      sent: failedOutright ? DASH : preview ? { value: RUNS.notSent, tone: "muted" } : { value: run.counts.sent.toLocaleString() },
      failed: failedOutright || preview ? DASH : count(run.counts.failed),
      skipped: failedOutright ? DASH : count(run.counts.skipped + run.counts.held, "warning"),
    },
    startedBy: startedByOf(run),
    openLabel: RUNS.openLabel(label),
  };
};

/** The list's "Last run" cell: the chip, when and how, what went wrong, and the meter. */
export const toLastRunView = (run: RunSummaryApi, timeZone: string) => {
  const state = runStateOf(run);
  const at = runMomentIso(run);
  const trigger = TRIGGER[run.trigger];
  const sub =
    state.key === "live"
      ? LIST_RUNS.started(run.started_at ? formatTimeOfDay(run.started_at, timeZone) : "", trigger)
      : LIST_RUNS.when(at ? formatShortDate(at, timeZone) : "", trigger);
  return {
    chip: state.chip,
    sub,
    detail: state.key === "ok" || state.key === "live" ? null : state.detail,
    meter: runMeterOf(run),
    live: state.key === "live",
    openLabel: LIST_RUNS.openRun(runLabelOf(run, timeZone)),
  };
};

/** Who started a run, in a sentence: "Schedule", a name, or "Someone". */
const starterName = (run: Pick<RunSummaryApi, "trigger" | "requested_by">): string => startedByOf(run).name;

const PHASE_ORDER = ["check", "make", "files", "send"] as const;

/**
 * The live progress card of a run that is going (prototype `liveCard`): its four phases, what it
 * is doing now, its counts and the bar. "Check files" can't be seen from the run's rows, so it reads
 * as done once sending starts.
 */
export const toRunLivePanel = (
  detail: RunDetailApi,
  timeZone: string,
): Omit<ProgressPanelProps, "titleId" | "onAction" | "headingRef" | "actionLabel" | "className"> => {
  const { run, deliveries } = detail;
  const progress = runProgressOf(detail);
  const current = progress.phase === "check" ? 0 : progress.phase === "make" ? 1 : progress.phase === "send" ? 3 : 4;
  const stateAt = (index: number): ProgressStep["state"] => (index < current ? "done" : index === current ? "current" : "todo");
  const asOf = run.data_as_of ? `${formatMonthDay(run.data_as_of, timeZone)}, ${formatTimeOfDay(run.data_as_of, timeZone)}` : null;
  const detailOf: Record<(typeof PHASE_ORDER)[number], string> = {
    check: current > 0 ? (asOf ? LIVE.stepDetail.fresh(asOf) : LIVE.stepDetail.filesDone) : LIVE.stepDetail.checking,
    make: LIVE.stepDetail.made(progress.made, progress.copies),
    files: current > 2 ? LIVE.stepDetail.filesDone : LIVE.stepDetail.filesTodo,
    send: LIVE.stepDetail.sent(progress.finished, progress.deliveries),
  };
  const nowText =
    progress.phase === "make"
      ? LIVE.now.make(progress.made, progress.copies)
      : progress.phase === "send"
        ? LIVE.now.send(progress.finished, progress.deliveries)
        : LIVE.now.check;
  const phase = progress.phase === "done" ? "send" : progress.phase;
  const share = progress.phase === "done" ? 1 : runProgressShare(phase, phase === "make" ? progress.made : progress.finished, phase === "make" ? progress.copies : progress.deliveries);
  const count = (status: string) => deliveries.filter((delivery) => delivery.status === status).length;
  const at = run.started_at ? formatTimeOfDay(run.started_at, timeZone) : null;
  return {
    title: LIVE.title(TRIGGER[run.trigger]),
    caption: at ? LIVE.caption(starterName(run), at) : LIVE.captionNoTime(starterName(run)),
    steps: PHASE_ORDER.map((id, index) => ({ id, label: LIVE.steps[id], detail: detailOf[id], state: stateAt(index) })),
    stepsLabel: LIVE.stepsLabel,
    stepStateLabels: LIVE.stepStates,
    nowText,
    countsText: LIVE.counts(count("sent"), count("failed"), count("skipped") + count("held")),
    percent: Math.round(share * 100),
    progressLabel: LIVE.progressLabel,
  };
};

/** The Share menu's line: how many are set up, how many are sending, how many need attention; null with none. */
export const toDistributionShareNote = (summaries: DistributionSummaryApi[]): string | null => {
  if (!summaries.length) return null;
  const sending = summaries.filter((summary) => summary.last_run && isLiveStatus(summary.last_run.status)).length;
  const attention = summaries.filter((summary) => summary.last_run && runCategoryOf(runStateOf(summary.last_run).key) === "attn").length;
  return [SHARE_NOTE.setUp(summaries.length), sending ? SHARE_NOTE.sending(sending) : null, attention ? SHARE_NOTE.attention(attention) : null].filter(Boolean).join(" · ");
};

